import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useInsights, type BrandInsight } from "@/hooks/use-insights";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Check, Lightbulb, Sparkles, X } from "lucide-react";
import { useLocale, formatDate } from "@/i18n/format";

function formatPeriod(periodStart: string, periodEnd: string, locale: string): string {
  try {
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const startLabel = formatDate(start, locale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    const endLabel = formatDate(end, locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    return `${startLabel} – ${endLabel}`;
  } catch {
    return `${periodStart} – ${periodEnd}`;
  }
}

function safeList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

interface InsightsPanelProps {
  brandId: string;
}

export function InsightsPanel({ brandId }: InsightsPanelProps) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  const { data, isLoading, isError } = useInsights(brandId, 12);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (isLoading) {
    return <Skeleton className="h-40 w-full rounded-lg" />;
  }
  // The panel must never break Analytics — fail silently.
  if (isError || !data) return null;

  if (data.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <EmptyState
          icon={Sparkles}
          title={t("insights.emptyTitle")}
          description={t("insights.emptyDesc")}
        />
      </div>
    );
  }

  const selected: BrandInsight =
    data.find((insight) => insight.id === selectedId) ?? data[0];
  const wins = safeList(selected.details?.wins);
  const losses = safeList(selected.details?.losses);
  const recommendations = safeList(selected.details?.recommendations);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide">
          <Sparkles size={14} />
          {t("insights.title")}
        </h2>
        <select
          value={selected.id}
          onChange={(e) => setSelectedId(e.target.value)}
          className="rounded border bg-background px-2 py-1 text-xs"
          aria-label={t("insights.periodLabel")}
        >
          {data.map((insight) => (
            <option key={insight.id} value={insight.id}>
              {formatPeriod(insight.periodStart, insight.periodEnd, locale)}
            </option>
          ))}
        </select>
      </div>

      <p className="text-sm">{selected.summary}</p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {wins.length > 0 && (
          <div className="space-y-1">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("insights.wins")}</h3>
            <ul className="space-y-1">
              {wins.map((win, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm">
                  <Check size={12} className="mt-1 flex-shrink-0 text-green-400" />
                  <span>{win}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {losses.length > 0 && (
          <div className="space-y-1">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("insights.losses")}</h3>
            <ul className="space-y-1">
              {losses.map((loss, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm">
                  <X size={12} className="mt-1 flex-shrink-0 text-red-400" />
                  <span>{loss}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {recommendations.length > 0 && (
          <div className="space-y-1">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t("insights.recommendations")}
            </h3>
            <ul className="space-y-1">
              {recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm text-muted-foreground">
                  <Lightbulb size={12} className="mt-1 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
